import React from 'react';
import sitemasterLogo from "../../../assets/SiteMaster.svg";
import {
  Drawer,
  Button,
  Typography,
  Box,
  Chip
} from '@mui/material';

const ViewTicketModal = ({ open, onClose, ticket }) => {
  if (!ticket) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      });
    } catch (e) {
      return '—';
    }
  };

  const title = ticket.title || ticket.issue_header || 'Issue title';
  const ticketId = ticket.ticketId || ticket.ticket_id || `#TICKET-${ticket.id}`;
  const createdAt = ticket.createdAt || formatDate(ticket.created_on || ticket.created_at);
  const respondedOn = ticket.respondedOn || formatDate(ticket.responded_on || ticket.modified_on);
  const resolutionBy = ticket.resolutionBy || formatDate(ticket.resolution_by);

  const description = ticket.issue_description || ticket.description || null;

  const rawAttachments = ticket.attachments ?? ticket.attachment;
  const attachmentList = Array.isArray(rawAttachments)
    ? rawAttachments.filter(Boolean)
    : rawAttachments
      ? [rawAttachments]
      : [];

  const getFileName = (fileUrl) => {
    if (!fileUrl || typeof fileUrl !== "string") return "Attachment";
    try {
      const pathWithoutQuery = fileUrl.split("?")[0];
      const fileName = pathWithoutQuery.substring(pathWithoutQuery.lastIndexOf("/") + 1);
      return decodeURIComponent(fileName || "Attachment");
    } catch (e) {
      return "Attachment";
    }
  };

  const timelineSteps = [
    {
      label: "Request received",
      date: createdAt,
      filled: true,
    },
    {
      label: "Responded on",
      date: respondedOn,
      filled: respondedOn && respondedOn !== '—',
    },
    {
      label: "Resolution by",
      date: resolutionBy,
      filled: false,
      dot: true,
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          borderRadius: '16px 0 0 16px',
          overflow: 'hidden',
          boxShadow: '-12px 0 48px rgba(59,0,105,0.18)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(145deg, #6C2BD7 0%, #4a1a9a 45%, #3B0069 100%)',
            pt: 3.5,
            pb: 3,
            px: 3,
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {/* Decorative orbs */}
          <Box sx={{
            position: 'absolute', top: -50, right: -50, width: 160, height: 160,
            borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -30, left: -30, width: 100, height: 100,
            borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none',
          }} />

          {/* Close button */}
          <Box
            onClick={onClose}
            sx={{
              position: 'absolute', top: 14, right: 14,
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', fontSize: 16, fontWeight: 300,
              transition: 'background 0.2s',
              '&:hover': { background: 'rgba(255,255,255,0.25)' },
            }}
          >
            ✕
          </Box>

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, position: 'relative' }}>
            <img
              src={sitemasterLogo}
              alt="SiteMaster Logo"
              style={{ height: 24, filter: 'brightness(0) invert(1)' }}
            />
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontFamily: 'Outfit, sans-serif' }}>
              Customer Support
            </Typography>
          </Box>

          {/* Ticket ID */}
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.55)',
              letterSpacing: '0.08em',
              mb: 0.75,
              position: 'relative',
            }}
          >
            {ticketId}
          </Typography>

          {/* Title */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 17,
              color: '#fff',
              lineHeight: 1.35,
              fontFamily: 'Outfit, sans-serif',
              position: 'relative',
              pr: 4,
              mb: 2,
            }}
          >
            {title}
          </Typography>

          {/* Meta row */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', position: 'relative' }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              background: 'rgba(255,255,255,0.12)', borderRadius: 999,
              px: 1.5, py: 0.5,
            }}>
              <span style={{ fontSize: 10 }}>📅</span>
              <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.85)', fontFamily: 'Outfit, sans-serif' }}>
                Raised {createdAt}
              </Typography>
            </Box>
            {ticket.status && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                background: 'rgba(255,255,255,0.12)', borderRadius: 999,
                px: 1.5, py: 0.5,
              }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: '#2AD13B', display: 'inline-block',
                }} />
                <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.85)', fontFamily: 'Outfit, sans-serif' }}>
                  {ticket.status}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: 'auto', background: '#fafafa', px: 3, py: 3 }}>

          {/* Timeline */}
          <Box
            sx={{
              background: '#fff',
              borderRadius: 3,
              border: '1px solid #ede8f8',
              p: 3,
              mb: 3,
              boxShadow: '0 1px 6px rgba(59,0,105,0.06)',
            }}
          >
            <Typography sx={{
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: '#3B0069', mb: 2.5, fontFamily: 'Outfit, sans-serif',
            }}>
              Ticket Timeline
            </Typography>

            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Connector line */}
              <Box sx={{
                position: 'absolute',
                top: 9,
                left: '16.5%',
                right: '16.5%',
                height: 2,
                background: 'linear-gradient(to right, #3B0069, #6C2BD7, #a78bcc)',
                borderRadius: 1,
                zIndex: 0,
              }} />

              {timelineSteps.map((step, i) => (
                <Box key={i} sx={{ textAlign: 'center', zIndex: 1, flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    {step.dot ? (
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: '2px solid #3B0069', background: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#3B0069' }} />
                      </Box>
                    ) : step.filled ? (
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: '#3B0069',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: '#fff', fontWeight: 700,
                      }}>
                        ✓
                      </Box>
                    ) : (
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: '2px solid #3B0069', background: '#fff',
                      }} />
                    )}
                  </Box>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 600, color: '#374151',
                    fontFamily: 'Outfit, sans-serif', lineHeight: 1.3, mb: 0.5,
                  }}>
                    {step.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: 12, fontWeight: 600, color: '#3B0069',
                    fontFamily: 'Outfit, sans-serif',
                  }}>
                    {step.date || '—'}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Duration row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2.5, borderTop: '1px solid #f0ebfc' }}>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography sx={{ fontSize: 11, color: '#9ca3af', fontFamily: 'Outfit, sans-serif' }}>
                  Response time
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1f2937', fontFamily: 'Outfit, sans-serif' }}>
                  2 Days
                </Typography>
              </Box>
              <Box sx={{ width: 1, background: '#f0ebfc', mx: 2 }} />
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography sx={{ fontSize: 11, color: '#9ca3af', fontFamily: 'Outfit, sans-serif' }}>
                  Expected resolution
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1f2937', fontFamily: 'Outfit, sans-serif' }}>
                  10 Days
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: '#3B0069', mb: 1.25, fontFamily: 'Outfit, sans-serif',
              display: 'flex', alignItems: 'center', gap: 0.75,
            }}>
              <span>📄</span> Issue Description
            </Typography>
            {description ? (
              <Box sx={{
                background: '#fff', border: '1px solid #ede8f8', borderRadius: 2.5,
                p: 2, fontSize: 13.5, color: '#374151', lineHeight: 1.65,
                boxShadow: '0 1px 4px rgba(59,0,105,0.05)', fontFamily: 'Outfit, sans-serif',
              }}>
                {description}
              </Box>
            ) : (
              <Box sx={{
                background: '#fff', border: '1px dashed #ddd6f5', borderRadius: 2.5,
                p: 2.5, textAlign: 'center',
              }}>
                <Typography sx={{ fontSize: 13, color: '#9ca3af', fontFamily: 'Outfit, sans-serif' }}>
                  No description provided
                </Typography>
              </Box>
            )}
          </Box>

          {/* Uploads */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: '#3B0069', mb: 1.25, fontFamily: 'Outfit, sans-serif',
              display: 'flex', alignItems: 'center', gap: 0.75,
            }}>
              <span>📎</span> Attachments
              {attachmentList.length > 0 && (
                <Box component="span" sx={{
                  background: '#ede8f8', color: '#3B0069', fontSize: 10, fontWeight: 700,
                  borderRadius: 999, px: 1, py: 0.25, ml: 0.5,
                }}>
                  {attachmentList.length}
                </Box>
              )}
            </Typography>
            {attachmentList.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {attachmentList.map((fileUrl, index) => (
                  <Box
                    key={`${fileUrl}-${index}`}
                    component="a"
                    href={fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      background: '#fff', border: '1px solid #ede8f8', borderRadius: 2,
                      p: 1.5, textDecoration: 'none',
                      boxShadow: '0 1px 3px rgba(59,0,105,0.06)',
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        background: '#f5f0fe',
                        borderColor: '#c4adf0',
                        boxShadow: '0 2px 8px rgba(59,0,105,0.12)',
                      },
                    }}
                  >
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #ede8f8, #d8ccf5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, fontSize: 16,
                    }}>
                      📎
                    </Box>
                    <Typography sx={{
                      fontSize: 13, color: '#3B0069', fontWeight: 500, flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontFamily: 'Outfit, sans-serif',
                    }}>
                      {getFileName(fileUrl)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#a78bcc' }}>›</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{
                background: '#fff', border: '1px dashed #ddd6f5', borderRadius: 2.5,
                p: 2.5, textAlign: 'center',
              }}>
                <Typography sx={{ fontSize: 13, color: '#9ca3af', fontFamily: 'Outfit, sans-serif' }}>
                  No attachments
                </Typography>
              </Box>
            )}
          </Box>

        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #f0ebfc', background: '#fff', flexShrink: 0 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 999,
              color: '#3B0069',
              borderColor: '#3B0069',
              fontWeight: 600,
              fontFamily: 'Outfit, sans-serif',
              py: 1.25,
              '&:hover': {
                borderColor: '#3B0069',
                backgroundColor: '#f3e9ff',
              },
            }}
          >
            Close
          </Button>
        </Box>

      </Box>
    </Drawer>
  );
};

export default ViewTicketModal;